import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'
import { EventModel } from '@/app/models/newDelegation/eventModel'
import { TripEventType } from '@/app/constants/delegation/tripEventType'
import { tmsEventKey } from '@/app/common/tmsDataHelper'
import { toast } from '@/lib/toasts/api'
import TmsLoadsEditor from '@/modules/drivers/shared/tms/TmsLoadsEditor'
import { useTmsLoadsEditor } from '@/modules/drivers/shared/tms/useTmsLoadsEditor'
import { TmsImportHeader } from './TmsImportHeader'

interface TmsImportViewProps {
  companyId: string
  driverId: number
  dateFrom: Moment
  dateTo: Moment
  onClose: () => void
  onSave: (events: EventModel[]) => void
  translate: (key: string) => string
  importedDocNumbers?: Set<string>
}


export const TmsImportView: React.FC<TmsImportViewProps> = ({
  companyId,
  driverId,
  dateFrom: initialDateFrom,
  dateTo: initialDateTo,
  onClose,
  onSave,
  translate,
  importedDocNumbers,
}) => {
  const editor = useTmsLoadsEditor({
    companyId,
    driverId,
    initialDateFrom: initialDateFrom.format('YYYY-MM-DD'),
    initialDateTo: initialDateTo.format('YYYY-MM-DD'),
    isInitiallyChecked: (item) =>
      !!importedDocNumbers && !!item.docNumber && importedDocNumbers.has(item.docNumber),
  })
  const { isLoading } = editor

  const handleSave = async () => {
    if (!editor.validateSelection()) return
    const hadChanges = editor.hasChanges
    if (!(await editor.persistChanges())) return

    const selectedItems = editor.selectedItems
    if (selectedItems.length === 0 && hadChanges) {
      onClose()
      return
    }
    if (selectedItems.length === 0) {
      toast.error({
        text: translate('newDelegation.no-items-selected'),
      })
      return
    }

    const events: EventModel[] = []
    const existingKeys = new Set<string>()
    let lpCounter = 0

    for (const item of selectedItems) {
      // Create Loading event
      if (item.loadingDate && item.loadingCountry) {
        const loadingKey = tmsEventKey(TripEventType.Loading, item.docNumber, item.loadingDate)
        if (!existingKeys.has(loadingKey)) {
          const loadingEvent: EventModel = {
            date: moment(item.loadingDate),
            tripEvent: TripEventType.Loading,
            country: item.loadingCountry,
            docNumber: item.docNumber || undefined,
            tmsRouteType: item.routeType,
            secondDate: item.unloadingDate ? moment(item.unloadingDate) : null,
            secondCountry: item.unloadingCountry || null,
            lp: lpCounter++,
          }
          events.push(loadingEvent)
          existingKeys.add(loadingKey)
        }
      }

      // Create Unloading event
      if (item.unloadingDate && item.unloadingCountry) {
        const unloadingKey = tmsEventKey(TripEventType.Unloading, item.docNumber, item.unloadingDate)
        if (!existingKeys.has(unloadingKey)) {
          const unloadingEvent: EventModel = {
            date: moment(item.unloadingDate),
            tripEvent: TripEventType.Unloading,
            country: item.unloadingCountry,
            docNumber: item.docNumber || undefined,
            tmsRouteType: item.routeType,
            secondDate: item.loadingDate ? moment(item.loadingDate) : null,
            secondCountry: item.loadingCountry || null,
            lp: lpCounter++,
          }
          events.push(unloadingEvent)
          existingKeys.add(unloadingKey)
        }
      }
    }

    if (events.length > 0) {
      onSave(events)
      toast.success({
        text: translate('newDelegation.tms-events-created'),
      })
    } else {
      toast.warning({
        text: translate('newDelegation.no-events-created'),
      })
    }
  }

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-bg-white">
      <TmsImportHeader onBack={onClose} translate={translate} />

      <TmsLoadsEditor editor={editor} companyId={companyId} />

      {/* Footer with Save/Cancel */}
      <div className="tw-flex tw-items-center tw-justify-end tw-gap-4 tw-border-t tw-px-6 tw-py-4">
        <button
          onClick={onClose}
          className="hover:tw-bg-base-50 tw-rounded-lg tw-border tw-border-base-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-content-base"
        >
          {translate('answer.cancel')}
        </button>
        <button
          onClick={handleSave}
          disabled={isLoading}
          className="tw-rounded-lg tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-primary/90 disabled:tw-opacity-50"
        >
          {translate('menu.save')}
        </button>
      </div>
    </div>
  )
}
